import { useEffect, useState } from 'react';
import { testimonials as fallbackTestimonials } from '@/mocks/homeData';
import { useScrollReveal, useScrollRevealMultiple } from '@/hooks/useScrollReveal';
import { Testimonial, fetchPublicTestimonials } from '@/services/testimonials';

function toDisplayItem(item: Testimonial) {
  return { id: item.id, name: item.name, handle: item.handle, content: item.content, avatar: item.avatar, isVerified: item.isVerified };
}

export default function Testimonials() {
  const [items, setItems] = useState(fallbackTestimonials.map((item) => ({ ...item, isVerified: false })));

  useEffect(() => {
    let alive = true;
    fetchPublicTestimonials().then((data) => {
      if (alive && data.length > 0) setItems(data.map(toDisplayItem));
    });
    return () => {
      alive = false;
    };
  }, []);

  const columns = [
    items.filter((_, i) => i % 3 === 0),
    items.filter((_, i) => i % 3 === 1),
    items.filter((_, i) => i % 3 === 2),
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { setRef, visible } = useScrollRevealMultiple(items.length);
  let globalIndex = 0;

  return (
    <section className="bg-[#FAF8F5] py-10 md:py-14">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-8 md:mb-12 scroll-reveal ${headerVisible ? 'scroll-reveal-visible' : ''}`}
        >
          <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
            使用者好評
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A] leading-tight">
            真實回饋，聽見美好改變
          </h2>
        </div>

        {/* Masonry grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {columns.map((col, colIndex) => (
            <div key={colIndex} className="flex flex-col gap-4 md:gap-5">
              {col.map((item) => {
                const idx = globalIndex++;
                const delayClass = `scroll-reveal-delay-${Math.min(idx + 1, 6)}`;
                return (
                  <div
                    key={item.id}
                    ref={setRef(idx)}
                    className={`bg-white rounded-xl p-6 md:p-7 border border-[#E8E0D6] hover:border-[#D4A574]/50 hover:shadow-md transition-all duration-300 scroll-reveal ${delayClass} ${visible[idx] ? 'scroll-reveal-visible' : ''}`}
                  >
                    {/* 引言符號 */}
                    <div className="w-8 h-8 flex items-center justify-center mb-4">
                      <i className="ri-double-quotes-l text-xl text-[#D4A574]/60"></i>
                    </div>
                    <p className="text-[#5A6B5A] text-base leading-relaxed">
                      {item.content}
                    </p>
                    {/* 分隔線 */}
                    <div className="mt-5 pt-5 border-t border-[#E8E0D6]/70">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#E8E0D6] bg-[#1B3D2A]/10 flex items-center justify-center">
                          {item.avatar ? (
                            <img
                              src={item.avatar}
                              alt={item.name}
                              className="w-full h-full object-cover object-top"
                            />
                          ) : (
                            <span className="text-sm font-bold text-[#1B3D2A]">{item.name.slice(0, 1)}</span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 text-sm font-semibold text-[#1B3D2A]">
                            {item.name}
                            {item.isVerified && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-[#1B3D2A]/8 px-1.5 py-0.5 text-[10px] font-bold text-[#1B3D2A]">
                                <i className="ri-shield-check-fill text-[11px]" />
                                已驗證顧客
                              </span>
                            )}
                          </div>
                          {item.handle && <div className="text-sm text-[#9A8A7A]">@{item.handle}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}